/* eslint-disable no-case-declarations */
import {
	animate,
	disableScroll,
	enableScroll,
	enableFocusTrap,
	disableFocusTrap,
	setCookie,
	getCookie,
	deleteCookiesWithPrefix,
} from './helpers';

export class Popup {
	/**
	 * Whether the popup has been initialized or not.
	 */
	hasBeenInitialized = false;

	/**
	 * Initialize the popup.
	 * @param {node} el .em-popup element
	 */
	constructor(el) {
		if (el) {
			this.el = el;
			this.cookieName = `em_popup_${el.id}`;
			this.cookieDuration = el.dataset.expiration;

			if (this.getCookie()) {
				this.destroy();
			} else {
				this.init();
			}
		}
	}

	/**
	 * Initialize the popup triggers.
	 */
	init = () => {
		const {
			triggers, enterAnimation, type,
		} = this.el.dataset;

		// Get the popup elements.
		this.overlayEl = this.el.querySelector('.em-popup__overlay');
		this.contentEl = this.el.querySelector('.em-popup__content');
		this.closeEl = this.el.querySelector('.em-popup__close');

		// Set the popup type and triggers.
		this.type = type;
		this.triggers = JSON.parse(triggers);
		this.enterAnimation = enterAnimation;

		// Placeholders for timers and event handlers so they can be cleared later.
		this.timeTriggers = [];
		this.scrollTriggers = [];
		this.exitTriggers = [];

		this.closeEl.addEventListener('click', this.hide);

		// Add event listener for the escape key.
		switch (this.type) {
			case 'modal':
				document.addEventListener('keyup', this.handleKeyup);
				this.overlayEl.addEventListener('click', this.hide);
				break;

			default:
				break;
		}

		this.triggers.forEach((settings) => {
			const { trigger } = settings;
			switch (trigger) {
				case 'time':
					const timer = setTimeout(() => {
						this.show();
					}, settings.time);
					this.timeTriggers.push(timer);
					break;

				case 'scroll':
					const scrollHandler = () => {
						if (this.canBeShown()) {
							// Get the height of the entire page (document)
							const totalHeight = document.body.scrollHeight - window.innerHeight;
							// Calculate the current scroll percentage
							const scrollPercentage = (window.scrollY / totalHeight) * 100;

							if (scrollPercentage >= settings.scroll_amount) {
								this.show();
								// Remove the event listener once the popup is shown.
								window.removeEventListener('scroll', scrollHandler);
							}
						}
					};

					window.addEventListener('scroll', scrollHandler);
					this.scrollTriggers.push(scrollHandler);
					break;

				case 'exit':
					const exitHandler = () => {
						if (this.canBeShown()) {
							// User has moved the mouse out of the viewport or left the window
							this.show();
							// Remove the event listener once the popup is shown.
							document.documentElement.removeEventListener('mouseleave', exitHandler);
						}
					};

					document.documentElement.addEventListener('mouseleave', exitHandler);
					this.exitTriggers.push(exitHandler);
					break;

				default:
					break;
			}
		});

		this.hasBeenInitialized = true;
	}

	/**
	 * Maybe hide the popup when the escape key is pressed.
	 * @param {Event} e The keyup event.
	 */
	handleKeyup = (e) => {
		if (e.key === 'Escape') {
			this.hide();
		}
	}

	/**
	 * Show the popup.
	 */
	show = () => {
		if (this.canBeShown()) {
			embark.components.popups.openPopup = this;
			this.el.setAttribute('aria-hidden', 'false');
			this.el.setAttribute('tabindex', '0');
			this.el.classList.add('show');
			animate(this.overlayEl, 'fadeIn');
			animate(this.contentEl, this.enterAnimation, '0.3s');

			switch (this.type) {
				case 'modal':
					disableScroll();
					enableFocusTrap(this.el);
					break;

				default:
					break;
			}
		}
	}

	/**
	 * Hide the popup.
	 */
	hide = () => {
		if (this.isVisible()) {
			let exitAnimation = 'fadeOut';
			switch (this.type) {
				case 'side':
				case 'banner':
					exitAnimation = this.enterAnimation.replace('In', 'Out');
					break;

				default:
					break;
			}

			animate(this.el, exitAnimation).then(() => {
				this.el.classList.remove('show');
				this.overlayEl.style.visibility = 'hidden';
				this.contentEl.style.visibility = 'hidden';

				switch (this.type) {
					case 'modal':
						enableScroll();
						disableFocusTrap();
						break;

					default:
						break;
				}

				this.setCookie();
				embark.components.popups.openPopup = null;
			});
		}
	}

	/**
	 * Check if the popup is visible.
	 * @returns {boolean} Whether the popup is visible or not.
	 */
	isVisible = () => this.el.classList.contains('show')

	/**
	 * Whether the popup can be shown or not.
	 * @returns {boolean} Whether the popup can be shown or not.
	 */
	canBeShown = () => {
		if (
			embark.components.popups.openPopup
			|| this.isVisible()
			|| this.getCookie()
		) {
			return false;
		}

		return true;
	}

	/**
	 * Set the popup cookie.
	 */
	setCookie = () => {
		setCookie(this.cookieName, 'true', this.cookieDuration);
	}

	/**
	 * Get the popup cookie.
	 * @returns {string} The popup cookie.
	 */
	getCookie = () => getCookie(this.cookieName)

	/**
	 * Destroy the popup.
	 */
	destroy = () => {
		if (this.hasBeenInitialized) {
			// Remove event listeners.
			this.closeEl.removeEventListener('click', this.hide);
			this.overlayEl.removeEventListener('click', this.hide);
			document.removeEventListener('keyup', this.handleKeyup);

			// Clear timers.
			this.timeTriggers.forEach((timer) => {
				clearTimeout(timer);
			});

			// Clear scroll handlers.
			this.scrollTriggers.forEach((handler) => {
				window.removeEventListener('scroll', handler);
			});

			// Clear exit handlers.
			this.exitTriggers.forEach((handler) => {
				document.removeEventListener('mouseleave', handler);
			});

			// Remove popup from the global array.
			embark.components.popups = embark.components.popups.filter((popup) => popup.el !== this.el);
		}

		// Remove popup from the DOM.
		this.el.remove();
	}
}

// Initialize the global embark.components.popups array.
embark.components.popups = {
	// All instances of the Popup class.
	instances: [],
	// The current open popup. Used to avoid opening multiple popups at the same time.
	openPopup: null,
	// Helper function to delete all popup cookies.
	deleteCookies: () => deleteCookiesWithPrefix('em_popup_'),
};

// Initialize popups.
export const initPopups = () => {
	// Get all .em-popup elements.
	const popups = [...document.querySelectorAll('.em-popup')];
	if (popups.length) {
		// Create a Popup instance for each .em-popup element.
		embark.components.popups.instances = [
			...embark.components.popups.instances,
			...popups.map((popup) => new Popup(popup)),
		];
	}
};

// Create a MutationObserver to watch for new popups.
const observer = new MutationObserver((mutations) => {
	mutations.forEach((mutation) => {
		if (mutation.addedNodes.length) {
			mutation.addedNodes.forEach((node) => {
				if (node && node.classList && node.classList.contains('em-popups-container')) {
					// Initialize popups.
					initPopups();
					observer.disconnect();
				}
			});
		}
	});
});

// Observe the body for new popups.
observer.observe(document.body, {
	childList: true,
	subtree: true,
});

// Initialize popups on page load.
initPopups();
